import {
  getFieldCrops,
  getFirstInstallationDate,
} from '../../managers/distributionManager';
import {
  getRecommendation,
  isSupportingPlannerByWeeks,
} from '../../managers/fieldManager';
const { plannerType } = require('@managers/planningManager');

var distributionActions = require('@actions/distributionActions');
var {
  setNetworkError,
  clearNetworkError,
} = require('@actions/networkErrorActions');
var distributionManager = require('@managers/distributionManager');
var farmingUnitManager = require('@managers/farmingUnitManager');
var fieldManager = require('@managers/fieldManager');
var sensorsManager = require('@managers/sensorsManager');
var GeneralClasses = require('@model/GeneralClasses');
var { error_message } = require('@managers/helpers');
var {
  getItem,
  setItem,
  removeItem,
} = require('@managers/localStorageManager.jsx');
var { getMixpanelInstance } = require('@managers/mixpanelManager');

// historical_data (crop water availability) is real-time and must never be
// persisted — strip it at every level when serializing distributions for the
// cache. A JSON.stringify replacer does this in one pass without cloning or
// mutating the in-memory tree (which keeps historical_data for the session).
var stringifyDistributionsForPersist = data =>
  JSON.stringify(data, (key, value) =>
    key === 'historical_data' ? undefined : value
  );

// Monotonic request id for load_distributions. Guards against stale responses
// from earlier requests overwriting newer field updates.
let lastLoadDistributionsRequestId = 0;
// While true, selected_entities skips history so farming_unit/field history does
// not compete with the priority GET distribution/{id} on the API connection pool.
let deferHistoryDuringInitialTreeLoad = false;

// In-flight dedup for load_distribution_by_id — concurrent callers (e.g.
// notifications filter + notification click) share one GET per distributionId.
const pendingDistributionTreeLoads = {};

const DISTRIBUTIONS_CACHE_KEY = 'DISTRIBUTIONS_DATA';

// Cache freshness. parseDistributionsCache guards on schema version only, and
// every refetch path short-circuits on isFullDistributionTree, so a tree
// hydrated once (e.g. via ensure_all_distributions_loaded) is otherwise served
// from IndexedDB indefinitely — field renames, moved/added sensors and crop
// changes stay stale all session on any non-selected distribution. We keep
// serving the cached tree instantly, but once the cache we painted is older
// than this we revalidate a full tree in the background the next time it is
// requested (stale-while-revalidate).
const DISTRIBUTIONS_CACHE_STALE_MS = 60 * 60 * 1000; // 1h

// savedAt of the cache painted at cold start (null when we did not paint from
// cache). Trees fetched fresh from the network this session are recorded so
// they are not re-flagged as stale; this is coarse (one timestamp for the whole
// payload) but enough to trigger a background refresh past the age above.
let cachedDistributionsSavedAt = null;
const distributionsRevalidatedThisSession = new Set();

const markDistributionTreeFresh = distributionId => {
  if (distributionId) {
    distributionsRevalidatedThisSession.add(distributionId);
  }
};

const isFullDistributionTreeStale = distributionId => {
  if (
    cachedDistributionsSavedAt == null ||
    distributionsRevalidatedThisSession.has(distributionId)
  ) {
    return false;
  }
  return Date.now() - cachedDistributionsSavedAt > DISTRIBUTIONS_CACHE_STALE_MS;
};

// Derive the lightweight [{id, name}] switcher index from a distributions map,
// sorted by name (tie-break id) to match the backend skeleton ordering. The
// cache no longer stores a separate index — it is derived from the data, and
// there is no separate Redux state slice for it either (see
// selectDistributionsIndex below for the memoized version consumers use).
export const deriveIndexFromDistributions = distributionsMap => {
  if (!distributionsMap) {
    return [];
  }
  return Object.values(distributionsMap)
    .filter(d => d?.id)
    .map(d => ({ id: d.id, name: d.name || '' }))
    .sort(
      (a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()) ||
        a.id.localeCompare(b.id)
    );
};

// Single-slot memoized selector for the switcher index. Consumers (dropdowns,
// entity selectors) call this from mapStateToProps/useSelector instead of
// reading a separately-dispatched Redux slice — recomputes only when the
// distributions map reference actually changes, so it stays a stable
// reference across unrelated re-renders (avoids extra renders from a fresh
// array on every store update).
let _distributionsIndexMemoInput = null;
let _distributionsIndexMemoOutput = [];
export const selectDistributionsIndex = distributionsMap => {
  if (distributionsMap === _distributionsIndexMemoInput) {
    return _distributionsIndexMemoOutput;
  }
  _distributionsIndexMemoInput = distributionsMap;
  _distributionsIndexMemoOutput =
    deriveIndexFromDistributions(distributionsMap);
  return _distributionsIndexMemoOutput;
};

const parseDistributionsCache = raw => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    // Schema guard (version, not TTL). Only the distributions map is required;
    // the switcher index is derived from it on read.
    if (
      parsed?.version === 2 &&
      parsed.distributions &&
      Object.keys(parsed.distributions).length
    ) {
      return parsed;
    }
  } catch (e) {
    // Best-effort: unexpected shape → ignore and load from server
  }
  return null;
};

const stringifyDistributionsCache = cachePayload =>
  stringifyDistributionsForPersist(cachePayload);

const resolveCurrentDistributionId = async getState => {
  const state = getState();
  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  return cachedId || null;
};

// Resolve selected distribution id as early as possible for the t=0 by-id fetch.
const resolveSelectedDistIdForFetch = async (
  getState,
  selectedDistributionId,
  cacheReadPromise,
  shouldUseCache
) => {
  if (selectedDistributionId) {
    return selectedDistributionId;
  }

  const state = getState();
  const fromEntities =
    state.distribution_data.selected_entities.distribution?.id;
  if (fromEntities) {
    return fromEntities;
  }

  const pending = state.distribution_data.pending_push_selection;
  if (pending?.distribution_id) {
    return pending.distribution_id;
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    const distributionId = urlParams.get('distributionId');
    if (distributionId) {
      return distributionId;
    }
  }

  const cachedId = await getItem('SELECTED_DISTRIBUTION_ID', null);
  if (cachedId) {
    return cachedId;
  }

  if (shouldUseCache) {
    const raw = await cacheReadPromise;
    const cache = parseDistributionsCache(raw);
    if (cache?.currentId) {
      return cache.currentId;
    }
  }

  return null;
};

const deriveSelectedDistIdFromState = (
  state,
  selectedDistributionId,
  cache,
  currentId
) => {
  const distributionsMap =
    state.distribution_data.distributions?.distributions || {};
  return (
    selectedDistributionId ||
    state.distribution_data.selected_entities.distribution?.id ||
    (cache?.currentId && cache.currentId in distributionsMap
      ? cache.currentId
      : null) ||
    (currentId && currentId in distributionsMap ? currentId : null) ||
    deriveIndexFromDistributions(distributionsMap)[0]?.id ||
    null
  );
};

const mergeSelectedDistributionTree = (
  dispatch,
  getState,
  requestId,
  selectedDistId,
  tree
) => {
  if (
    requestId !== lastLoadDistributionsRequestId ||
    !tree ||
    !selectedDistId
  ) {
    return false;
  }

  const merged = mergeDistributionsResult(getState(), {
    distributions: { [selectedDistId]: tree },
  });
  dispatch(set_all_distributions(merged));
  markDistributionTreeFresh(selectedDistId);

  deferHistoryDuringInitialTreeLoad = false;

  const selectedDist = merged.distributions?.[selectedDistId];
  if (distributionManager.isFullDistributionTree(selectedDist)) {
    dispatch(distributionActions.selected_entities(null, null, null, null));
  }

  setImmediate(async () => {
    try {
      await persistDistributionsCache(getState(), selectedDistId);
    } catch (e) {
      console.error('Failed to cache distributions:', e);
    }
  });

  dispatch(clearNetworkError(false));
  dispatch(set_first_all_distributions_load_status('success'));
  return true;
};

const mergeDistributionsResult = (state, incoming) => {
  const existing = state.distribution_data.distributions?.distributions || {};
  const incomingMap = incoming.distributions || {};
  // Shallow-merge already-parsed distribution trees. Re-running
  // GeneralClasses.Distributions over in-memory trees used to corrupt kc_table
  // month values (API flat data.jan vs client data.byMonth.jan).
  const merged = new GeneralClasses.Distributions({});
  merged.distributions = { ...existing, ...incomingMap };
  return merged;
};

// Smart skeleton merge. The skeleton is the source of truth for structure and
// light fields (name, role) and for membership: new distributions / farming
// units are added, ones that disappeared are pruned, and a changed name/role is
// taken from the skeleton. Already-loaded heavy detail (farming-unit fields,
// sensors, historical_data, and any other distribution attributes) is preserved
// — the skeleton never clobbers a full tree.
const mergeSkeletonIntoDistributions = (existingMap, skeletonList) => {
  const result = {};

  for (const skel of skeletonList) {
    if (!skel?.id) {
      continue;
    }
    const prev = existingMap[skel.id];
    const skelFus = skel.farming_units || {};

    if (!prev) {
      // Brand-new distribution — skeleton only until it is selected/loaded.
      result[skel.id] = { ...skel };
      continue;
    }

    const prevFus = prev.farming_units || {};
    const mergedFus = {};
    for (const fuId in skelFus) {
      const prevFu = prevFus[fuId];
      if (!prevFu) {
        // New farming unit — skeleton only.
        mergedFus[fuId] = { ...skelFus[fuId] };
      } else {
        // Keep loaded detail (fields, sensors, historical_data); take the new
        // light fields (name, role) from the skeleton.
        mergedFus[fuId] = {
          ...prevFu,
          name: skelFus[fuId].name,
          role: skelFus[fuId].role,
        };
      }
    }
    // Farming units present before but absent from the skeleton are pruned.

    result[skel.id] = {
      ...prev,
      name: skel.name,
      role: skel.role,
      farming_units: mergedFus,
    };
  }
  // Distributions present before but absent from the skeleton are pruned.

  return result;
};

const persistDistributionsCache = async (state, currentId) => {
  const distributions =
    state.distribution_data.distributions?.distributions || {};
  if (!Object.keys(distributions).length) {
    return;
  }

  await setItem(
    DISTRIBUTIONS_CACHE_KEY,
    stringifyDistributionsCache({
      version: 2,
      savedAt: Date.now(),
      distributions,
      currentId:
        currentId ||
        state.distribution_data.selected_entities.distribution?.id ||
        deriveIndexFromDistributions(distributions)[0]?.id,
    })
  );
};

// Drop the persisted distributions cache and the in-memory tree map. Units are
// baked into the tree server-side, so on a unit-system change both the
// IndexedDB cache and the Redux map hold trees in the old units and must be
// discarded before the fresh (new-unit) load. Awaiting the removeItem before
// re-loading matters: an in-flight load's setImmediate(persistDistributionsCache)
// could otherwise rewrite the cache after we deleted it.
export var clear_distributions_cache = () => async dispatch => {
  await removeItem(DISTRIBUTIONS_CACHE_KEY);
  dispatch(set_all_distributions(null));
};

// Helper function to check if farming unit history is already loading
const isFarmingUnitHistoryLoading = (getState, farmingUnitId) => {
  const farmingUnitLoadingObject =
    getState().distribution_data.farming_unit_load_history;
  return (
    farmingUnitLoadingObject.status === 'init' &&
    farmingUnitLoadingObject.farming_unit_ids.includes(farmingUnitId)
  );
};

// Helper function to dispatch history loads
const dispatchHistoryLoads = (
  dispatch,
  distributionId,
  farmingUnitId,
  fieldId = null
) => {
  dispatch(get_farming_units_history(distributionId, farmingUnitId, null));
  setTimeout(() => {
    dispatch(get_farming_units_recommendations(distributionId, farmingUnitId));
  }, 0);
  if (fieldId) {
    dispatch(get_field_history(distributionId, farmingUnitId, fieldId));
  }
};

// Helper function to load history if not already loading
const loadHistoryIfNeeded = (
  dispatch,
  getState,
  distributionId,
  farmingUnitId,
  fieldId = null
) => {
  if (!isFarmingUnitHistoryLoading(getState, farmingUnitId)) {
    dispatchHistoryLoads(dispatch, distributionId, farmingUnitId, fieldId);
  }
};

// Fire-and-forget full-tree refresh for the selected distribution (master-style:
// paginated .then). Does not block load_distributions or the UI shell.
const refreshSelectedDistributionInBackground = (
  dispatch,
  getState,
  requestId,
  selectedDistId
) => {
  if (!selectedDistId) {
    return;
  }

  distributionManager.getDistributionById(selectedDistId).then(
    tree => {
      if (
        !mergeSelectedDistributionTree(
          dispatch,
          getState,
          requestId,
          selectedDistId,
          tree
        )
      ) {
        return;
      }
    },
    err => {
      if (requestId !== lastLoadDistributionsRequestId) {
        return;
      }
      deferHistoryDuringInitialTreeLoad = false;
      console.log(err);
      console.error(
        '[Distributions] selected distribution load error:',
        err.message
      );
    }
  );
};

// Middleware
export var load_distributions = (
  force_load = false,
  shouldUseCache = false,
  selectedDistributionId = null,
  selectedFarmingUnitId = null,
  selectedFieldId = null
) => {
  return async (dispatch, getState) => {
    let state = getState();
    const requestId = ++lastLoadDistributionsRequestId;

    // Load only once (unless forced)
    if (state.distribution_data.distributions && !force_load) {
      return;
    }

    dispatch(set_first_all_distributions_load_status('init'));
    dispatch(set_selected_distribution_network_refresh_status('pending'));
    deferHistoryDuringInitialTreeLoad = true;

    // t=0 — skeleton, cache read, and GET distribution/{id} all start together
    // (same pattern as the old summary path: no await-before-fetch chain).
    const cacheReadPromise = shouldUseCache
      ? getItem(DISTRIBUTIONS_CACHE_KEY)
      : Promise.resolve(null);

    const skeletonPromise = distributionManager
      .getDistributionsSkeleton()
      .catch(err => {
        console.log(err);
        console.error('[Distributions] skeleton error:', err.message);
        return null;
      });

    const selectedDistIdPromise = resolveSelectedDistIdForFetch(
      getState,
      selectedDistributionId,
      cacheReadPromise,
      shouldUseCache
    );

    const selectedTreePromise = selectedDistIdPromise.then(async distId => {
      if (!distId) {
        return { distId: null, tree: null };
      }
      try {
        const tree = await distributionManager.getDistributionById(distId);
        return { distId, tree };
      } catch (err) {
        console.log(err);
        console.error(
          '[Distributions] selected distribution load error:',
          err.message
        );
        return { distId, tree: null };
      }
    });

    let cache = null;
    let usableScreen = false;

    const cachePaintPromise = (async () => {
      if (!shouldUseCache) {
        return;
      }
      cache = parseDistributionsCache(await cacheReadPromise);
      if (!cache) {
        return;
      }
      // Age of the trees we are about to paint — drives background revalidation
      // of full trees when the user later requests them (isFullDistributionTreeStale).
      cachedDistributionsSavedAt = cache.savedAt ?? null;
      dispatch(
        set_all_distributions(
          new GeneralClasses.Distributions(cache.distributions)
        )
      );
      dispatch(distributionActions.selected_entities(null, null, null, null));
      state = getState();
      const se = state.distribution_data.selected_entities;
      if (se?.distribution?.id && se?.farming_unit?.id) {
        usableScreen = true;
        dispatch(set_first_all_distributions_load_status('success'));
      }
    })();

    const [skeleton, treeResult, , currentId] = await Promise.all([
      skeletonPromise,
      selectedTreePromise,
      cachePaintPromise,
      resolveCurrentDistributionId(getState),
    ]);

    if (requestId !== lastLoadDistributionsRequestId) {
      return;
    }

    const { distId: fetchedDistId, tree: selectedTree } = treeResult;

    if (skeleton && skeleton.length) {
      state = getState();
      const existingMap =
        state.distribution_data.distributions?.distributions || {};
      const mergedMap = mergeSkeletonIntoDistributions(existingMap, skeleton);
      dispatch(
        set_all_distributions(new GeneralClasses.Distributions(mergedMap))
      );
    }

    state = getState();
    const distributionsMap =
      state.distribution_data.distributions?.distributions || {};
    const selectedDistId = deriveSelectedDistIdFromState(
      state,
      selectedDistributionId,
      cache,
      currentId
    );

    let treeMerged = false;
    if (selectedTree && fetchedDistId) {
      treeMerged = mergeSelectedDistributionTree(
        dispatch,
        getState,
        requestId,
        fetchedDistId,
        selectedTree
      );
    }

    if (!usableScreen && skeleton && skeleton.length) {
      usableScreen = true;
    }
    if (!usableScreen && Object.keys(distributionsMap).length) {
      usableScreen = true;
    }

    state = getState();
    if (state.distribution_data.distributions?.distributions) {
      postDistributionLoadAnalytics(state.distribution_data.distributions);
    }

    if (usableScreen && !treeMerged) {
      dispatch(clearNetworkError(false));
      dispatch(set_first_all_distributions_load_status('success'));
    } else if (
      !usableScreen &&
      !skeleton &&
      !Object.keys(distributionsMap).length
    ) {
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(set_first_all_distributions_load_status('fail'));
      dispatch(set_selected_distribution_network_refresh_status('fail'));
    }

    const backgroundRefreshStarted =
      !!selectedDistId && (!treeMerged || selectedDistId !== fetchedDistId);

    // Fallback when id was unknown at t=0 or the parallel fetch failed.
    if (backgroundRefreshStarted) {
      refreshSelectedDistributionInBackground(
        dispatch,
        getState,
        requestId,
        selectedDistId
      );
    } else if (!treeMerged) {
      deferHistoryDuringInitialTreeLoad = false;
      dispatch(set_selected_distribution_network_refresh_status('fail'));
    }
  };
};

export var load_distribution_by_id = distributionId => {
  return async (dispatch, getState) => {
    const state = getState();
    const existing =
      state.distribution_data.distributions?.distributions?.[distributionId];
    // A skeleton entry already has farming_units (id/name/role only), so the
    // presence of farming_units is not enough — only skip the fetch when the
    // full tree (fields on every farming unit) is already loaded. Full but
    // stale trees fall through to a background refresh below.
    const isFull = distributionManager.isFullDistributionTree(existing);
    if (isFull && !isFullDistributionTreeStale(distributionId)) {
      return existing;
    }

    let loadPromise = pendingDistributionTreeLoads[distributionId];
    if (!loadPromise) {
      loadPromise = (async () => {
        try {
          const tree =
            await distributionManager.getDistributionById(distributionId);
          const merged = mergeDistributionsResult(getState(), {
            distributions: {
              [distributionId]: tree,
            },
          });
          dispatch(set_all_distributions(merged));
          markDistributionTreeFresh(distributionId);
          return merged.distributions[distributionId];
        } finally {
          if (pendingDistributionTreeLoads[distributionId] === loadPromise) {
            delete pendingDistributionTreeLoads[distributionId];
          }
        }
      })();
      pendingDistributionTreeLoads[distributionId] = loadPromise;
    }

    // Stale-while-revalidate: we already have the full tree but the cache it
    // came from is old — paint it immediately and let the refresh finish in the
    // background (errors are non-fatal; the stale tree stays usable).
    if (isFull) {
      loadPromise.catch(() => {});
      return existing;
    }

    return loadPromise;
  };
};

// Above this many missing trees, one bulk pass beats N by-id requests. A root
// account can see dozens of distributions, and firing a request per distribution
// saturates the browser's per-host connection pool and the API alike. Below it,
// by-id is cheaper: it fetches only what is missing, whereas the bulk route
// always returns whole pages.
const BULK_LOAD_THRESHOLD = 4;

// Cross-distribution screens (technician assistant, transmitter search, remote
// configuration modal) need every distribution's full field tree, not just the
// selected one — they resolve fields and sensors by id across all customers.
// With the skeleton-first load only the selected distribution is hydrated, so
// these screens call this once on open to fill in the rest.
//
// Already-full trees are never refetched: when nothing is missing this returns
// immediately, so reopening a screen costs nothing.
export var ensure_all_distributions_loaded = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const distributionsMap =
      state.distribution_data.distributions?.distributions || {};
    const idsToLoad = Object.keys(distributionsMap).filter(
      id => !distributionManager.isFullDistributionTree(distributionsMap[id])
    );

    if (!idsToLoad.length) {
      return;
    }

    if (idsToLoad.length < BULK_LOAD_THRESHOLD) {
      await Promise.all(
        idsToLoad.map(id => dispatch(load_distribution_by_id(id)))
      );
      return;
    }

    // Bulk path: the legacy paginated route, with the meteorological station
    // scan turned off — these screens never read meteorological_sources, and
    // that scan is the single most expensive part of the old payload.
    try {
      const bulk = await distributionManager.distributions(1, 3, false);
      const bulkMap = bulk?.distributions || {};

      // Take ONLY the trees that were missing. mergeDistributionsResult merges
      // per distribution id, so an incoming entry replaces the existing one
      // wholesale — and a freshly fetched tree carries no historical_data.
      // Letting the bulk payload overwrite already-hydrated distributions would
      // blank out graphs on screens that are already open.
      const missingTrees = {};
      for (const id of idsToLoad) {
        if (bulkMap[id]) {
          missingTrees[id] = bulkMap[id];
        }
      }

      if (Object.keys(missingTrees).length) {
        dispatch(
          set_all_distributions(
            mergeDistributionsResult(getState(), {
              distributions: missingTrees,
            })
          )
        );
        Object.keys(missingTrees).forEach(markDistributionTreeFresh);
        return;
      }
    } catch (err) {
      console.error('[Distributions] bulk load failed:', err.message);
    }

    // Bulk failed or came back empty — fall back to per-distribution loads so
    // the screen still works rather than silently showing nothing.
    await Promise.all(
      idsToLoad.map(id => dispatch(load_distribution_by_id(id)))
    );
  };
};

export var selected_entities = (distribution, farming_unit, field, sensors) => {
  return async (dispatch, getState) => {
    var state = getState();
    let is_archive_mode = state.distribution_data.field_state === 'archive';

    // Check if URL params are present - if so, skip localStorage selection and url params will be used
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasUrlParams =
        urlParams.has('distributionId') && urlParams.has('farmingUnitId');
      if (hasUrlParams && !distribution && !farming_unit && !field) {
        // URL params exist and no explicit entities passed - skip localStorage loading
        // selected_entities_by_id will be called shortly with the correct entities
        return;
      }
    }

    // Only await AsyncStorage if we need the cached values
    // We only need cached values if there's no current Redux state AND no explicit parameters
    let cachedDistributionId = null;
    let cachedFarmingUnitId = null;
    let cachedFieldId = null;
    let cachedSensorIds = null;

    const hasCurrentState =
      state.distribution_data.selected_entities.distribution ||
      state.distribution_data.selected_entities.farming_unit ||
      state.distribution_data.selected_entities.field;

    const hasExplicitParams = distribution || farming_unit || field || sensors;

    // Only block on AsyncStorage if we have no current state and no explicit parameters
    // (i.e., app startup or first load)
    const needsCachedValues = !hasCurrentState && !hasExplicitParams;

    if (needsCachedValues) {
      if (state.distribution_data.pending_push_selection) {
        return;
      }

      // Only block on AsyncStorage if we actually need the cached values
      [
        cachedDistributionId,
        cachedFarmingUnitId,
        cachedFieldId,
        cachedSensorIds,
      ] = await Promise.all([
        getItem('SELECTED_DISTRIBUTION_ID', null),
        getItem('SELECTED_FARMING_UNIT_ID', null),
        getItem('SELECTED_FIELD_ID', null),
        getItem('SELECTED_SENSOR_TRANSMITTER_IDS', null),
      ]);

      state = getState();
      if (state.distribution_data.pending_push_selection) {
        return;
      }
      // Note: History loading is deferred until after entities are validated and set
      // to avoid race conditions. See shouldLoadSelectedFarmingUnitData logic below.
    }

    // Distribution
    var selected_distribution = distribution;

    if (!selected_distribution) {
      var selected_distribution_id = undefined;

      if (state.distribution_data.selected_entities.distribution) {
        selected_distribution_id =
          state.distribution_data.selected_entities.distribution.id;
      } else if (cachedDistributionId != null) {
        selected_distribution_id = cachedDistributionId;
      }
      if (state.distribution_data?.distributions?.distributions === undefined) {
        selected_distribution = null;
      } else if (
        selected_distribution_id in
        state.distribution_data?.distributions?.distributions
      ) {
        selected_distribution =
          state.distribution_data.distributions.distributions[
            selected_distribution_id
          ];
      } else {
        for (var first_distribution_id in state.distribution_data.distributions
          .distributions)
          break; // Take the first distribution
        selected_distribution =
          state.distribution_data.distributions.distributions[
            first_distribution_id
          ];
      }

      if (
        !selected_distribution &&
        state.distribution_data?.distributions?.distributions
      ) {
        return;
      }
    }

    // Farming Unit
    var selected_farming_unit = farming_unit;

    if (!selected_farming_unit && selected_distribution) {
      var selected_farming_unit_id = undefined;

      if (state.distribution_data.selected_entities.farming_unit) {
        selected_farming_unit_id =
          state.distribution_data.selected_entities.farming_unit.id;
      } else if (cachedFarmingUnitId != null) {
        selected_farming_unit_id = cachedFarmingUnitId;
      } else {
        // Deep links / by-id may have written the intended FU while selection
        // only held a skeleton (or null FU). Read it so the full-tree reselect
        // can finish resolving after GET distribution/{id}.
        selected_farming_unit_id = await getItem(
          'SELECTED_FARMING_UNIT_ID',
          null
        );
      }

      if (
        selected_farming_unit_id &&
        selected_farming_unit_id in selected_distribution.farming_units
      ) {
        selected_farming_unit =
          selected_distribution.farming_units[selected_farming_unit_id];
      } else {
        for (var first_farming_unit_id in selected_distribution.farming_units)
          break; // Take the first farming unit
        selected_farming_unit =
          selected_distribution.farming_units[first_farming_unit_id];
      }
    }

    // Never commit a skeleton FU (no `fields`) into selected_entities — the
    // same rule as optimistic distribution switch. Keep the intended id in
    // storage; the next full-tree selected_entities call resolves it.
    if (selected_farming_unit && !selected_farming_unit.fields) {
      if (selected_farming_unit.id) {
        setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
      }
      selected_farming_unit = null;
    }

    // Field
    var selected_field = field;

    // A skeleton farming unit (from the structural skeleton load, not yet
    // hydrated with a full distribution tree) has no `fields` key at all.
    // Treat that as "not ready to resolve a field" rather than crashing on
    // Object.entries(undefined) — the field resolves once the full tree lands.
    if (
      !selected_field &&
      selected_farming_unit &&
      selected_farming_unit.fields
    ) {
      var selected_field_id = undefined;

      if (state.distribution_data.selected_entities.field) {
        selected_field_id = state.distribution_data.selected_entities.field.id;
      } else if (cachedFieldId != null) {
        selected_field_id = cachedFieldId;
      } else {
        // Monday / URL deep links persist the field id before the tree is
        // full; pick it up on the post-hydration reselect.
        selected_field_id = await getItem('SELECTED_FIELD_ID', null);
      }

      // filter just active fields from farmingUnit fields ( field_state is active )
      let filtered_ids = Object.fromEntries(
        Object.entries(selected_farming_unit.fields).filter(
          ([k, field]) => field.settings.field_state === 'active'
        )
      );

      if (is_archive_mode) {
        // when we toggle to archive mode , filter to archive fields only !
        filtered_ids = Object.fromEntries(
          Object.entries(selected_farming_unit.fields).filter(
            ([k, field]) => field.settings.field_state === 'archive'
          )
        );
      }

      if (selected_field_id && selected_field_id in filtered_ids) {
        selected_field = selected_farming_unit.fields[selected_field_id];
      } else {
        for (var first_field_id in filtered_ids) break; // Take the first field
        selected_field = selected_farming_unit.fields[first_field_id];
      }
    }

    let shouldLoadSelectedFarmingUnitData = false;
    let shouldLoadSelectedFieldData = false;
    const currentRecommendations =
      getState().distribution_data.farming_unit_recommendations.recommendations;
    const userLengthUnits = getState().authentication_data.user_data.length;
    const fieldRecommendation =
      selected_field &&
      currentRecommendations &&
      currentRecommendations[selected_field.id];
    const hasRecommendationsForField =
      fieldRecommendation && fieldRecommendation.units === userLengthUnits;
    if (
      (selected_farming_unit &&
        !isFarmingUnitHistoryLoading(getState, selected_farming_unit.id)) ||
      (isSupportingPlannerByWeeks(selected_field) &&
        !hasRecommendationsForField)
    ) {
      // and no history for this field
      // load field history
      shouldLoadSelectedFarmingUnitData = true;
    }

    // Defer history loading to avoid blocking navigation
    // Use setTimeout to make history loading non-blocking
    if (
      shouldLoadSelectedFarmingUnitData &&
      selected_distribution &&
      selected_farming_unit &&
      !deferHistoryDuringInitialTreeLoad
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      setTimeout(() => {
        dispatchHistoryLoads(dispatch, distId, fuId);
      }, 0);
      shouldLoadSelectedFieldData = true;
    }

    // Optimized: Compare by ID instead of expensive JSON.stringify
    if (
      selected_field &&
      (!state.distribution_data.selected_entities.field ||
        state.distribution_data.selected_entities.field.id !==
          selected_field.id)
    ) {
      shouldLoadSelectedFieldData = true;

      // Create new sensors object to avoid mutating Redux state
      const updatedSensors = Object.values(selected_field.sensors).reduce(
        (acc, currentSensor) => {
          acc[currentSensor.id] = {
            ...currentSensor,
            state: GeneralClasses.SENSOR_STATES.INIT,
          };
          return acc;
        },
        {}
      );
      selected_field = {
        ...selected_field,
        sensors: updatedSensors,
      };
    }

    if (
      shouldLoadSelectedFieldData &&
      selected_distribution &&
      selected_farming_unit &&
      selected_field
    ) {
      const distId = selected_distribution.id;
      const fuId = selected_farming_unit.id;
      const fId = selected_field.id;
      setTimeout(() => {
        dispatch(get_field_history(distId, fuId, fId));
      }, 0);
    }

    // Sensor
    var selected_sensors = sensors;

    if (!selected_sensors && selected_field) {
      var selected_sensor_transmitter_ids = undefined;

      if (cachedSensorIds != null) {
        selected_sensor_transmitter_ids = cachedSensorIds.split(',');

        let field_transmitter_ids = sensorsManager.get_transmitter_ids(
          selected_field.sensors
        );

        // find the stored sensors objects in the current field instance
        let checkSensorsArray = selected_sensor_transmitter_ids.map(
          function (sensor_id) {
            if (field_transmitter_ids.includes(sensor_id)) {
              let selectedSensor = Object.values(selected_field.sensors).filter(
                a => a.transmitter_id == sensor_id
              );
              return selectedSensor;
            } else {
              return undefined;
            }
          }
        );

        if (checkSensorsArray.includes(undefined)) {
          selected_sensors = {};
        } else {
          selected_sensors = selected_sensor_transmitter_ids.reduce(function (
            map,
            obj
          ) {
            let sensor = Object.values(selected_field.sensors).filter(
              a => a.transmitter_id == obj
            )[0];
            map[sensor.id] = sensor;
            return map;
          }, {});
        }
      } else {
        selected_sensors = {};
      }
    }

    // Don't await setItem - fire and forget to avoid blocking UI
    if (selected_distribution) {
      setItem('SELECTED_DISTRIBUTION_ID', selected_distribution.id);
    }
    if (selected_farming_unit) {
      setItem('SELECTED_FARMING_UNIT_ID', selected_farming_unit.id);
    }
    if (selected_field) {
      setItem('SELECTED_FIELD_ID', selected_field.id);
    }

    let field_transmitter_ids =
      sensorsManager.get_transmitter_ids(selected_sensors);
    if (selected_sensors) {
      setItem('SELECTED_SENSOR_TRANSMITTER_IDS', field_transmitter_ids);
    } else {
      setItem('SELECTED_SENSORS_ID', null);
    }

    var mixpanelInstance = getMixpanelInstance();
    mixpanelInstance.register({
      Distribution: selected_distribution ? selected_distribution.name : null,
      'Distribution Id': selected_distribution
        ? selected_distribution.id
        : null,
      'Farming Unit': selected_farming_unit ? selected_farming_unit.name : null,
      'Farming Unit Id': selected_farming_unit
        ? selected_farming_unit.id
        : null,
      Field: selected_field ? selected_field.name : null,
      'Field Id': selected_field ? selected_field.id : null,
      'Early Season': fieldManager.isFieldEarlyDays(selected_field),
      'Crop Type': selected_field ? selected_field.crop_data.crop_type : null,
      'Soil Type': selected_field ? selected_field.soil_type : null,
      // 'Sensor Ids': selected_sensors ? selected_sensors.map(a => a.id) : null,
    });

    // v3.x API: use getPeople() instead of people
    mixpanelInstance.getPeople().set({
      'Last Distribution': selected_distribution
        ? selected_distribution.name
        : null,
      'Last Farming Unit': selected_farming_unit
        ? selected_farming_unit.name
        : null,
      'Last Field': selected_field ? selected_field.name : null,
    });

    dispatch(
      set_selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        selected_sensors
      )
    );
  };
};

export var postDistributionLoadAnalytics = distributions => {
  let mixpanelInstance = getMixpanelInstance();
  let lastInstallationDate = getFirstInstallationDate(
    distributions.distributions
  );
  let formattedInstallationTime = lastInstallationDate.format(
    'YYYY-MM-DD HH:mm:ss'
  );

  // v3.x API: use getPeople() instead of people
  mixpanelInstance.getPeople().set({
    'First Installation Date': formattedInstallationTime,
  });

  let allUserCrops = getFieldCrops(distributions.distributions);
  mixpanelInstance.getPeople().union('All user crops', {
    Crops: allUserCrops,
  });
};

export var selected_entities_by_id = (
  distribution_id,
  farming_unit_id,
  field_id = null
) => {
  return (dispatch, getState) => {
    var state = getState();

    // Persist intended ids immediately so a later full-tree selected_entities
    // reselect (after skeleton-first load) still lands on the deep-linked
    // field — e.g. Monday links with ?distributionId&farmingUnitId&fieldId.
    if (distribution_id) {
      setItem('SELECTED_DISTRIBUTION_ID', distribution_id);
    }
    if (farming_unit_id) {
      setItem('SELECTED_FARMING_UNIT_ID', farming_unit_id);
    }
    if (field_id) {
      setItem('SELECTED_FIELD_ID', field_id);
    }

    var selected_distribution = null;
    var selected_farming_unit = null;
    var selected_field = null;

    var all_distributions = state.distribution_data.distributions.distributions;
    if (all_distributions && distribution_id in all_distributions) {
      selected_distribution = all_distributions[distribution_id];
    }

    if (
      selected_distribution &&
      farming_unit_id in selected_distribution.farming_units
    ) {
      selected_farming_unit =
        selected_distribution.farming_units[farming_unit_id];
    }

    // Skeleton FUs omit `fields` — do not pass them into selected_entities
    // (it would either crash or commit a useless FU). Keep dist-only until
    // the full tree lands; storage ids above finish the resolve then.
    if (!selected_farming_unit?.fields) {
      if (selected_distribution) {
        dispatch(
          set_selected_entities(selected_distribution, null, null, null)
        );
      }
      if (distribution_id) {
        dispatch(load_distribution_by_id(distribution_id));
      }
      return;
    }

    if (field_id && field_id in selected_farming_unit.fields) {
      selected_field = selected_farming_unit.fields[field_id];
    }

    dispatch(
      distributionActions.selected_entities(
        selected_distribution,
        selected_farming_unit,
        selected_field,
        null
      )
    );
  };
};

export var delete_distribution = distribution_id => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.delete_distribution(distribution_id).then(
        function (result) {
          dispatch(distribution_action_success('DELETE'));
          dispatch(load_distributions(true));
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('DELETE', err));
        }
      );
    }
  };
};

export var update_distribution = (distribution_id, distribution_name) => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        distribution_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this distribution"
          )
        )
      );
    } else {
      // create distribution
      distributionManager
        .update_distribution(distribution_id, distribution_name)
        .then(
          function (result) {
            dispatch(distribution_action_success('UPDATE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(distribution_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_distribution = distribution_name => {
  return (dispatch, getState) => {
    dispatch(initiate_distribution_action('CREATE'));

    if (!getState().authentication_data.user_details.is_root) {
      dispatch(
        distribution_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create distributions"
          )
        )
      );
    } else {
      // create distribution
      distributionManager.create_distribution(distribution_name).then(
        function (result) {
          dispatch(distribution_action_success('CREATE'));
          dispatch(load_distributions(true));
        },
        function (err) {
          console.log(err);
          dispatch(distribution_action_fail('CREATE', err));
        }
      );
    }
  };
};

export var set_field_state = field_state => {
  return (dispatch, getState) => {
    dispatch(set_field_state_action(field_state));
    dispatch(selected_entities(null, null, null, null));
  };
};

export var initiate_distribution_action = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_INIT',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_success = distribution_action_type => {
  return {
    type: 'DISTRIBUTION_ACTION_SUCCESS',
    distribution_action_type: distribution_action_type,
  };
};

export var distribution_action_fail = (distribution_action_type, err) => {
  return {
    type: 'DISTRIBUTION_ACTION_FAIL',
    distribution_action_type: distribution_action_type,
    error_message: error_message(err),
  };
};

export var clear_distribution_action = () => {
  return {
    type: 'CLEAR_DISTRIBUTION_ACTION',
  };
};

export var clear_distribution_data = () => {
  return {
    type: 'CLEAR_DISTRIBUTION_DATA',
  };
};

// ======

export var delete_farming_unit = (distribution_id, farming_unit_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this farming unit"
          )
        )
      );
    } else {
      // delete farming unit
      farmingUnitManager
        .delete_farming_unit(distribution_id, farming_unit_id)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('DELETE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit.id
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this farming unit"
          )
        )
      );
    } else {
      // update farming unit
      farmingUnitManager
        .update_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('UPDATE'));

            // Optimistically merge the saved farming unit (with its sensors)
            // into Redux immediately, so newly added sensors show right away
            // instead of only after the load_distributions re-pull lands past
            // GSI propagation. The re-pull below reconciles ids (tmp_ -> SC_N).
            dispatch(
              update_farming_unit_optimistic(distribution_id, farming_unit)
            );

            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_farming_unit = (distribution_id, farming_unit) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_action('CREATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        null
      )
    ) {
      dispatch(
        farming_unit_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a farming unit under this distribution"
          )
        )
      );
    } else {
      // create farming unit
      farmingUnitManager
        .create_farming_unit(distribution_id, farming_unit)
        .then(
          function (result) {
            dispatch(farming_unit_action_success('CREATE'));
            dispatch(load_distributions(true));
          },
          function (err) {
            console.log(err);
            dispatch(farming_unit_action_fail('CREATE', err));
          }
        );
    }
  };
};

/**
 * Refresh selected_entities with the latest data from distributions state.
 * Call after updating historical data so components that read from
 * selected_entities see the new data without requiring a field re-selection.
 */
function refreshSelectedEntities(
  dispatch,
  getState,
  distributionId,
  farmingUnitId
) {
  const state = getState();
  const selectedEntities = state.distribution_data.selected_entities;
  if (
    !selectedEntities.farming_unit ||
    selectedEntities.farming_unit.id !== farmingUnitId
  ) {
    return;
  }

  const updatedFarmingUnit =
    state.distribution_data.distributions?.distributions?.[distributionId]
      ?.farming_units?.[farmingUnitId];

  if (!updatedFarmingUnit) return;

  const selectedField = selectedEntities.field;
  const updatedField =
    selectedField && updatedFarmingUnit.fields?.[selectedField.id]
      ? updatedFarmingUnit.fields[selectedField.id]
      : selectedField;

  dispatch(
    set_selected_entities(
      selectedEntities.distribution,
      updatedFarmingUnit,
      updatedField,
      selectedEntities.sensors
    )
  );
}

export var get_farming_units_history = (
  distribution_id,
  farming_unit_id,
  field_id
) => {
  return (dispatch, getState) => {
    dispatch(initiate_farming_unit_history_load_action(farming_unit_id));

    farmingUnitManager
      .loadFarmingUnitHistory(distribution_id, farming_unit_id, field_id)
      .then(
        function (result) {
          let currentFarmingUnit =
            getState().distribution_data.distributions?.distributions?.[
              distribution_id
            ]?.farming_units?.[farming_unit_id];

          // Guard: If farming unit not found, just clear status and return
          if (!currentFarmingUnit) {
            console.log(
              'get_farming_units_history: farming unit not found in state'
            );
            dispatch(clear_status_farming_unit_history_load_action());
            return;
          }

          dispatch(
            update_farming_unit_historical_data(
              distribution_id,
              farming_unit_id,
              result['historicalData']
            )
          );

          refreshSelectedEntities(
            dispatch,
            getState,
            distribution_id,
            farming_unit_id
          );

          dispatch(success_farming_unit_history_load_action(farming_unit_id));
          dispatch(clear_status_farming_unit_history_load_action());
        },
        function (err) {
          console.log('get_farming_units_history error:', err);
          dispatch(clear_status_farming_unit_history_load_action());
        }
      );
  };
};

export var get_field_history = (distribution_id, farming_unit_id, field_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_history_load_action(field_id));

    fieldManager
      .loadFieldHistory(distribution_id, farming_unit_id, field_id)
      .then(
        function (result) {
          const historicalData = result['historicalData'][field_id];

          dispatch(
            update_field_historical_data(
              distribution_id,
              farming_unit_id,
              field_id,
              historicalData
            )
          );

          refreshSelectedEntities(
            dispatch,
            getState,
            distribution_id,
            farming_unit_id
          );

          dispatch(success_field_history_load_action(field_id));
          dispatch(clear_status_field_history_load_action());
        },
        function (err) {
          console.log(err);
        }
      );
  };
};

// ======

export var initiate_farming_unit_action = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_INIT',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_success = farming_unit_action_type => {
  return {
    type: 'FARMING_UNIT_ACTION_SUCCESS',
    farming_unit_action_type: farming_unit_action_type,
  };
};

export var farming_unit_action_fail = (farming_unit_action_type, err) => {
  return {
    type: 'FARMING_UNIT_ACTION_FAIL',
    farming_unit_action_type: farming_unit_action_type,
    error_message: error_message(err),
  };
};

export var clear_farming_unit_action = () => {
  return {
    type: 'CLEAR_FARMING_UNIT_ACTION',
  };
};

export var initiate_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_INIT',
    farming_unit_id: farming_unit_id,
  };
};

export var initiate_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_INIT',
    field_id: field_id,
  };
};

export var clear_farming_unit_history_load_action = () => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR',
    farming_unit_id: '',
  };
};

export var clear_status_farming_unit_history_load_action = () => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_CLEAR_STATUS',
  };
};

export var clear_status_field_history_load_action = () => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_CLEAR_STATUS',
  };
};

export var update_field_historical_data = (
  distributionId,
  farmingUnitId,
  fieldId,
  historicalData
) => {
  return {
    type: 'UPDATE_FIELD_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, fieldId, historicalData },
  };
};

export var update_farming_unit_historical_data = (
  distributionId,
  farmingUnitId,
  historicalData
) => {
  return {
    type: 'UPDATE_FARMING_UNIT_HISTORICAL_DATA',
    payload: { distributionId, farmingUnitId, historicalData },
  };
};

// Optimistically update field data in Redux immediately after API success,
// before load_distributions completes (which can take ~10 seconds)
export var update_field_optimistic = (
  distributionId,
  farmingUnitId,
  fieldObject
) => {
  return {
    type: 'UPDATE_FIELD_OPTIMISTIC',
    payload: { distributionId, farmingUnitId, fieldObject },
  };
};

export var update_farming_unit_optimistic = (distributionId, farmingUnit) => {
  return {
    type: 'UPDATE_FARMING_UNIT_OPTIMISTIC',
    payload: { distributionId, farmingUnit },
  };
};

export var success_farming_unit_history_load_action = farming_unit_id => {
  return {
    type: 'LOAD_FARMING_UNIT_HISTORY_ACTION_SUCCESS',
    farming_unit_id: farming_unit_id,
  };
};
export var success_field_history_load_action = field_id => {
  return {
    type: 'LOAD_FIELD_HISTORY_ACTION_SUCCESS',
    field_id: field_id,
  };
};

export var delete_field = (distribution_id, farming_unit_id, field_id) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('DELETE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'DELETE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to delete this field"
          )
        )
      );
    } else {
      // delete farming unit
      fieldManager
        .delete_field(distribution_id, farming_unit_id, field_id)
        .then(
          function (result) {
            dispatch(field_action_success('DELETE'));
            dispatch(load_distributions(true));

            var mixpanelInstance = getMixpanelInstance();

            mixpanelInstance.track('Field Delete');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('DELETE', err));
          }
        );
    }
  };
};

export var update_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    dispatch(initiate_field_action('UPDATE'));

    if (
      !distributionManager.is_admin(
        getState().distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      ) &&
      !farmingUnitManager.is_moderator(
        getState().distribution_data.selected_entities.farming_unit
      )
    ) {
      dispatch(
        field_action_fail(
          'UPDATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to update this field"
          )
        )
      );
    } else {
      // update field
      fieldManager
        .update_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('UPDATE'));

            // Optimistically update Redux state with the field data we just saved,
            // so the user sees the updated data immediately while load_distributions runs
            dispatch(
              update_field_optimistic(
                distribution_id,
                farming_unit_id,
                field_object
              )
            );

            dispatch(load_distributions(true));

            var mixpanelInstance = getMixpanelInstance();
            mixpanelInstance.track('Field Update');
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('UPDATE', err));
          }
        );
    }
  };
};

export var create_field = (distribution_id, farming_unit_id, field_object) => {
  return (dispatch, getState) => {
    var state = getState();

    dispatch(initiate_field_action('CREATE'));

    if (
      !distributionManager.is_admin(
        state.distribution_data.distributions.distributions,
        distribution_id,
        farming_unit_id
      )
    ) {
      dispatch(
        field_action_fail(
          'CREATE',
          new GeneralClasses.LocalError(
            "You don't have the right permissions to create a field under this farming unit"
          )
        )
      );
    } else {
      // create field
      fieldManager
        .create_field(distribution_id, farming_unit_id, field_object)
        .then(
          function (result) {
            dispatch(field_action_success('CREATE'));
            dispatch(
              load_distributions(
                true,
                false,
                null,
                null,
                result['fieldId'],
                null
              )
            );

            mixpanel.track('Field Create', {
              Field: result['fieldName'],
              'Field Id': result['fieldId'],
            });
          },
          function (err) {
            console.log(err);
            dispatch(field_action_fail('CREATE', err));
          }
        );
    }
  };
};

export var initiate_field_action = field_action_type => {
  return {
    type: 'FIELD_ACTION_INIT',
    field_action_type: field_action_type,
  };
};

export var field_action_success = field_action_type => {
  return {
    type: 'FIELD_ACTION_SUCCESS',
    field_action_type: field_action_type,
  };
};

export var field_action_fail = (field_action_type, err) => {
  return {
    type: 'FIELD_ACTION_FAIL',
    field_action_type: field_action_type,
    error_message: error_message(err),
  };
};

export var clear_field_action = () => {
  return {
    type: 'CLEAR_FIELD_ACTION',
  };
};

export var set_selected_entities = (
  selected_distribution,
  selected_farming_unit,
  selected_field,
  selected_sensors
) => {
  return {
    type: 'SET_SELECTED_ENTITIES',
    selected_distribution: selected_distribution,
    selected_farming_unit: selected_farming_unit,
    selected_field: selected_field,
    selected_sensors: selected_sensors,
  };
};

export var set_pending_push_selection = pending_push_selection => {
  return {
    type: 'SET_PENDING_PUSH_SELECTION',
    pending_push_selection,
  };
};

export var clear_pending_push_selection = () => {
  return {
    type: 'CLEAR_PENDING_PUSH_SELECTION',
  };
};

export var set_all_distributions = distributions => {
  return {
    type: 'SET_ALL_DISTRIBUTIONS',
    distributions: distributions,
  };
};

export const set_transmitter_coordinates = transmitter_coordinates => {
  return {
    type: 'SET_TRANSMITTER_COORDINATES',
    transmitter_coordinates,
  };
};

export var set_field_state_action = field_state => {
  return {
    type: 'SET_FIELD_STATE',
    field_state: field_state,
  };
};

export var set_first_all_distributions_load_status = status => {
  return {
    type: 'SET_FIRST_ALL_DISTRIBUTIONS_LOAD_STATUS',
    status: status,
  };
};

export var set_selected_distribution_network_refresh_status = status => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION_NETWORK_REFRESH_STATUS',
    status,
  };
};

export const set_selected_farming_unit = selected_farming_unit => {
  return {
    type: 'SET_SELECTED_FARMING_UNIT',
    selected_farming_unit,
  };
};

export const set_selected_distribution = selected_distribution => {
  return {
    type: 'SET_SELECTED_DISTRIBUTION',
    selected_distribution,
  };
};

// A cold load dispatches this alongside the history loads, before the full
// distribution tree has landed — at that point the farming unit is missing or
// skeleton-only and its fields carry no planner settings, so there is nothing
// to decide on yet. Retry until the tree arrives instead of giving up: without
// this, All Farm View shows no recommendations until the user re-selects a
// farming unit (which re-dispatches this thunk against a populated tree).
const RECOMMENDATIONS_TREE_RETRY_DELAY_MS = 500;
const RECOMMENDATIONS_TREE_MAX_RETRIES = 12;

const isFarmingUnitTreeReady = farmingUnit => {
  const fields = farmingUnit && farmingUnit.fields;
  if (!fields) return false;
  // Fields exist but a skeleton tree carries no settings — only once at least
  // one field has planner_settings can the weekly filter below be trusted.
  return Object.values(fields).some(
    field => field && field.settings && field.settings.planner_settings
  );
};

export const get_farming_units_recommendations = (
  distribution_id,
  farming_unit_id,
  attempt = 0
) => {
  return async (dispatch, getState) => {
    const state = getState();
    // Guard every hop: the distributions map can be null (e.g. just after
    // clear_distributions_cache on a unit-system change) and the distribution /
    // farming unit may be skeleton-only. Matches get_farming_units_history.
    const currentFarmingUnit =
      state.distribution_data.distributions?.distributions?.[distribution_id]
        ?.farming_units?.[farming_unit_id];

    if (
      !isFarmingUnitTreeReady(currentFarmingUnit) &&
      attempt < RECOMMENDATIONS_TREE_MAX_RETRIES
    ) {
      setTimeout(() => {
        dispatch(
          get_farming_units_recommendations(
            distribution_id,
            farming_unit_id,
            attempt + 1
          )
        );
      }, RECOMMENDATIONS_TREE_RETRY_DELAY_MS);
      return;
    }

    // Out of retries with still no fields — nothing to fetch. Leave whatever is
    // in the store alone rather than overwriting it with an empty map.
    if (!currentFarmingUnit || !currentFarmingUnit.fields) return;

    // A retry can land after the user moved on. The store keeps one
    // recommendations map for the whole app, so dispatching a late result for a
    // farming unit that is no longer selected would wipe the visible one.
    if (attempt > 0) {
      const selectedFarmingUnitId =
        getState().distribution_data.selected_entities?.farming_unit?.id;
      if (selectedFarmingUnitId && selectedFarmingUnitId !== farming_unit_id) {
        return;
      }
    }

    const fieldIds = Object.keys(currentFarmingUnit.fields);
    const recommendations = {};

    await Promise.all(
      fieldIds.map(async fieldId => {
        try {
          if (
            currentFarmingUnit.fields[fieldId]?.settings?.planner_settings
              ?.type === plannerType.WEEKLY_RECOMMENDATION
          ) {
            const fieldRec = await getRecommendation(fieldId);
            recommendations[fieldId] = fieldRec;
          }
        } catch (e) {
          recommendations[fieldId] = null;
        }
      })
    );

    dispatch({
      type: 'FARMING_UNIT_RECOMMENDATIONS_LOADED',
      payload: { farmingUnitId: farming_unit_id, recommendations },
    });
  };
};

export const update_field_recommendation_show_rate_line = (
  fieldId,
  showRateLine
) => {
  return {
    type: 'UPDATE_FIELD_RECOMMENDATION_SHOW_RATE_LINE',
    payload: { fieldId, showRateLine },
  };
};
